"use client";

import { useState, useEffect, useRef } from "react";
import { useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import Button from "./ui/Button";

interface ContactFormData {
  email: string;
  phone: string;
  name: string;
  companyName: string;
  comments: string;
}

export default function ContactForm() {
  const [contactMethod, setContactMethod] = useState<"email" | "phone" | null>(null);
  const [focusedField, setFocusedField] = useState<"email" | "phone" | null>(null);
  const [formData, setFormData] = useState<ContactFormData>({
    email: "",
    phone: "",
    name: "",
    companyName: "",
    comments: "",
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });
  
  const nameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const commentsInputRef = useRef<HTMLTextAreaElement>(null);
  const createProspectFromHomepage = useMutation(api.api.prospects.createProspectFromHomepage);

  // Check for prefilled email on component mount and when window regains focus
  useEffect(() => {
    const checkPrefillEmail = () => {
      const prefillEmail = sessionStorage.getItem("prefillEmail");
      const shouldFocusName = sessionStorage.getItem("focusNameField");
      
      if (prefillEmail) {
        setFormData((prev) => ({ ...prev, email: prefillEmail }));
        setContactMethod("email");
        sessionStorage.removeItem("prefillEmail");
      }
      
      if (shouldFocusName) {
        // Focus the name field after a short delay to allow scroll to complete
        setTimeout(() => {
          nameInputRef.current?.focus();
        }, 800);
        sessionStorage.removeItem("focusNameField");
      }
    };

    // Check on mount
    checkPrefillEmail();

    // Check when window regains focus (after scrolling)
    window.addEventListener("focus", checkPrefillEmail);
    
    // Also check periodically in case focus event doesn't fire
    const interval = setInterval(checkPrefillEmail, 500);

    return () => {
      window.removeEventListener("focus", checkPrefillEmail);
      clearInterval(interval);
    };
  }, []);

  // Focus appropriate input when contact method is selected
  useEffect(() => {
    if (contactMethod === "email") {
      setTimeout(() => emailInputRef.current?.focus(), 100);
    } else if (contactMethod === "phone") {
      setTimeout(() => phoneInputRef.current?.focus(), 100);
    }
  }, [contactMethod]);

  
  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validatePhone = (phone: string): boolean => {
    // Remove formatting and check if we have at least 10 digits
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    
    // Apply phone number formatting if it's the phone field
    const formattedValue = name === 'phone' ? formatPhoneNumber(value) : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue,
    }));
    
    // Clear any error messages when user starts typing
    if (submitStatus.type === "error") {
      setSubmitStatus({ type: null, message: "" });
    }

  };
  
  const validateForm = (): string | null => {
    // Validate contact method - only require one of email or phone
    const hasEmail = validateEmail(formData.email);
    const hasPhone = validatePhone(formData.phone);
    
    if (!hasEmail && !hasPhone) {
      return "Please provide either a valid email address or phone number";
    }
    
    // If email is provided, validate it
    if (formData.email.trim() && !hasEmail) {
      return "Please enter a valid email address";
    }
    
    // If phone is provided, validate it
    if (formData.phone.trim() && !hasPhone) {
      return "Please enter a valid phone number";
    }

    // Validate other required fields
    if (!formData.name.trim()) return "Name is required";
    if (!formData.companyName.trim()) return "Company name is required";
    if (!formData.comments.trim()) return "Comments are required";
    
    return null;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setSubmitStatus({
        type: "error",
        message: validationError,
      });
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: "" });
    
    try {
      // Strip phone number formatting before submission
      const cleanPhone = formData.phone.replace(/\D/g, '');
      
      await createProspectFromHomepage({
        name: formData.name,
        email: formData.email,
        phone: cleanPhone,
        companyName: formData.companyName,
        comments: formData.comments,
      });
      
      setSubmitStatus({
        type: "success",
        message: "Thank you! Your message has been sent successfully. We'll get back to you soon.",
      });
      
      // Reset form
      setFormData({
        email: "",
        phone: "",
        name: "",
        companyName: "",
        comments: "",
      });
      setContactMethod(null);
      setFocusedField(null);
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: error instanceof Error ? error.message : "Something went wrong. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const hasValidContact = validateEmail(formData.email) || validatePhone(formData.phone);
  const isFormValid = formData.name.trim() && 
                      formData.companyName.trim() && 
                      formData.comments.trim() &&
                      (validateEmail(formData.email) || validatePhone(formData.phone));

  return (
    <section id="contact" className="py-12 bg-slate-50 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="font-poppins text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            Ready to Own Your Presence?
          </h2>
          <p className="font-poppins text-sm text-gray-600 max-w-3xl mx-auto">
            Get started today, and find your business&apos;s Allegory
          </p>
        </div>

        <div className="flex justify-center">
          <div className="w-full max-w-2xl">
            <div className="bg-white p-8 border border-gray-200 rounded shadow-sm min-h-[400px] flex items-center justify-center">
              {submitStatus.type === "success" ? (
                <div className="font-poppins flex flex-col items-center justify-center gap-4">
                  <p className="text-xl font-bold text-gray-900 text-center">
                    We're so glad you reached out. We will contact you shortly!
                  </p>
                  <p className="text-base text-gray-600 text-center">
                    In the meantime, if you have any other questions you'd like to ask, or if there was something else you wanted us to know, feel free to reach out to us!
                  </p>
                  <Button onClick={() => window.location.href = "mailto:info@allegorysoftware.com"} size="sm">Send Us an Email</Button>
                </div>
              ) : (
              <form onSubmit={handleSubmit} noValidate className="w-full min-h-[400px] flex flex-col align-middle justify-center">
        <div className={`transition-all duration-[500ms] ease-out justify-center align-middle`}>
          <label className="block text-center font-poppins text-xl leading-relaxed font-medium text-gray-700 mb-4 transition-all duration-[500ms]">
            {hasValidContact ? "Anything Else?" : "How Should We Contact You?"}
          </label>
          
          <div className="space-y-4">
            {/* Email Input */}
            <div className="relative">
              <label 
                htmlFor="email" 
                className={`absolute left-4 bg-white px-2 rounded font-medium text-gray-700 font-poppins pointer-events-none transition-all duration-200 ease-out ${
                  formData.email.trim() || (contactMethod === "email" && focusedField === "email")
                    ? "-top-2 text-xs"
                    : contactMethod === "email"
                    ? "top-1/2 -translate-y-1/2 text-base"
                    : "top-1/2 -translate-y-1/2 text-base cursor-pointer"
                }`}
                onClick={() => {
                  if (contactMethod !== "email") {
                    setContactMethod("email");
                    setSubmitStatus({ type: null, message: "" });
                    setTimeout(() => emailInputRef.current?.focus(), 50);
                  }
                }}
              >
                Email
              </label>
              <input
                ref={emailInputRef}
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                onFocus={() => {
                  if (contactMethod !== "email") {
                    setContactMethod("email");
                  }
                  setFocusedField("email");
                }}
                onBlur={() => setFocusedField(null)}
                onClick={() => {
                  if (contactMethod !== "email") {
                    setContactMethod("email");
                    setSubmitStatus({ type: null, message: "" });
                  }
                }}
                required={contactMethod === "email" && !validatePhone(formData.phone)}
                    className={`w-full border rounded font-poppins text-base font-normal leading-relaxed outline-none transition-colors ${
                      formData.email.trim() || (contactMethod === "email" && focusedField === "email")
                        ? "px-4 py-4 border-gray-900 cursor-text text-left"
                        : contactMethod === "email"
                        ? "px-4 py-4 border-gray-300 focus:border-gray-900 cursor-text text-left"
                        : "px-4 py-4 border-gray-300 hover:border-gray-900 cursor-pointer text-center"
                    }`}
                placeholder=""
              />
            </div>

            {/* Phone Input */}
            <div className="relative">
              <label 
                htmlFor="phone" 
                className={`absolute left-4 bg-white px-2 rounded font-medium text-gray-700 font-poppins pointer-events-none transition-all duration-200 ease-out ${
                  formData.phone.trim() || (contactMethod === "phone" && focusedField === "phone")
                    ? "-top-2 text-xs"
                    : contactMethod === "phone"
                    ? "top-1/2 -translate-y-1/2 text-base"
                    : "top-1/2 -translate-y-1/2 text-base cursor-pointer"
                }`}
                onClick={() => {
                  if (contactMethod !== "phone") {
                    setContactMethod("phone");
                    setSubmitStatus({ type: null, message: "" });
                    setTimeout(() => phoneInputRef.current?.focus(), 50);
                  }
                }}
              >
                Phone
              </label>
              <input
                ref={phoneInputRef}
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                onFocus={() => {
                  if (contactMethod !== "phone") {
                    setContactMethod("phone");
                  }
                  setFocusedField("phone");
                }}
                onBlur={() => setFocusedField(null)}
                onClick={() => {
                  if (contactMethod !== "phone") {
                    setContactMethod("phone");
                    setSubmitStatus({ type: null, message: "" });
                  }
                }}
                required={contactMethod === "phone" && !validateEmail(formData.email)}
                    className={`w-full border rounded font-poppins text-base font-normal leading-relaxed outline-none transition-colors ${
                      formData.phone.trim() || (contactMethod === "phone" && focusedField === "phone")
                        ? "px-4 py-4 border-gray-900 cursor-text text-left"
                        : contactMethod === "phone"
                        ? "px-4 py-4 border-gray-300 focus:border-gray-900 cursor-text text-left"
                        : "px-4 py-4 border-gray-300 hover:border-gray-900 cursor-pointer text-center"
                    }`}
                placeholder=""
              />
            </div>
          </div>
        </div>

        {/* Name, Company, and Details - Show when contact info is valid */}
        <div className={`grid transition-all duration-[500ms] ease-out ${
          hasValidContact ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}>
          <div className="overflow-hidden">
            <div className="space-y-6 pt-6 flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block font-poppins text-base leading-relaxed font-medium text-gray-700 mb-2">
                  Your Name
                </label>
                <input
                  ref={nameInputRef}
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:border-gray-900 focus:outline-none transition-colors font-poppins text-base font-normal leading-relaxed"
                  placeholder="Enter your full name"
                />
              </div>
              
              <div>
                <label htmlFor="companyName" className="block font-poppins text-base leading-relaxed font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:border-gray-900 focus:outline-none transition-colors font-poppins text-base font-normal leading-relaxed"
                  placeholder="Your Company LLC"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="comments" className="block font-poppins text-base leading-relaxed font-medium text-gray-700 mb-2">
                Project Details & Comments
              </label>
              <textarea
                ref={commentsInputRef}
                id="comments"
                name="comments"
                rows={5}
                value={formData.comments}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded focus:border-gray-900 focus:outline-none transition-colors resize-vertical font-poppins text-base font-normal leading-relaxed"
                placeholder="Tell us about your project needs, timeline, budget, and any specific requirements..."
              />
            </div>
            </div>
          </div>
        </div>
        
        {/* Error Messages - Show only if there's an error */}
        {submitStatus.type === "error" && (
          <div className="p-4 border rounded bg-red-50 border-red-200 text-red-800 font-poppins text-base font-normal leading-relaxed">
            {submitStatus.message}
          </div>
        )}
        
        {/* Submit Button - Show when contact info is valid */}
        <div className={`grid transition-all duration-[500ms] ease-out ${
          hasValidContact ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}>
          <div className="overflow-hidden">
            <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting || !isFormValid}
              className={`w-full rounded px-6 py-3 text-base font-medium transition-colors ${
                isSubmitting || !isFormValid
                  ? "bg-gray-300 text-gray-500"
                  : "bg-gray-900 hover:bg-gray-800 text-white"
              }`}
            >
              {isSubmitting ? "Sending..." : "Let's Begin"}
            </button>
            
            <div className="text-center text-gray-600 font-poppins text-base font-normal leading-relaxed mt-4">
              <p>Free consultation • No obligation • Quick response within 24 hours</p>
            </div>
            </div>
          </div>
        </div>
      </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}